import React, { useEffect, useState } from 'react';
import { numericId } from '../../lib/avatars';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Button } from '../ui';
import { getThemeColors } from '../../lib/theme';
import { v2Matching, sortByAvailability, formatStartdatum, type MatchCandidate } from '../../lib/matching';
import { MatchScoreBadge } from './MatchScoreBadge';
import { MatchDetailsPopover } from './MatchDetailsPopover';
import { CareParamIconRow } from '../care/CareParamIcons';

/**
 * MatchingList — wiederverwendbare Kandidatenliste (PB ↔ BK) mit Score.
 *
 * Einsatzorte: PB-Dashboard-Widget, BK-Dashboard-Widget, Matching-Seite,
 * als Picker via `onSelect`. `mode='compact'` zeigt schmale Zeilen (Widgets),
 * `mode='full'` Karten mit mehr Details.
 *
 * BK-Kandidaten (PB-Anker) zeigen ihr Startdatum („Verfügbar ab") und sind
 * danach sortiert (früheste zuerst, gleicher Tag → Score). PB-Kandidaten
 * bleiben in der Score-Reihenfolge des Backends.
 */
export interface MatchingListProps {
  /** Anker-User (PB oder BK) — die Gegenrolle wird serverseitig ermittelt. */
  userId: number;
  dark: boolean;
  mode?: 'full' | 'compact';
  limit?: number;
  /** Wenn gesetzt, wird jede Zeile klickbar (Picker-Verwendung). */
  onSelect?: (candidate: MatchCandidate) => void;
  /** Mindest-Score-Filter (0 = alle). */
  minScore?: number;
}

function ageYears(geburtsdatum: string | null): number | null {
  if (!geburtsdatum) return null;
  const d = new Date(geburtsdatum);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
}

export function MatchingList({ userId, dark, mode = 'full', limit = 25, onSelect, minScore = 0 }: MatchingListProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [candidates, setCandidates] = useState<MatchCandidate[]>([]);
  const [anchorIncomplete, setAnchorIncomplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openBreakdown, setOpenBreakdown] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    v2Matching.candidatesFor(userId, limit)
      .then(res => {
        if (!alive) return;
        setCandidates(res.candidates);
        setAnchorIncomplete(res.anchor.incomplete);
      })
      .catch(() => { if (alive) setError(tr('matching.list.load_error')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, limit]);

  if (loading) {
    return <div style={{ fontSize: 12, color: t.muted, padding: '10px 0' }}>{tr('matching.list.loading')}</div>;
  }
  if (error) {
    return <div style={{ fontSize: 12, color: '#b42318', padding: '10px 0' }}>{error}</div>;
  }
  if (anchorIncomplete) {
    return (
      <div style={{ fontSize: 12, color: t.muted, padding: '10px 0' }}>
        {tr('matching.list.no_profile')}
      </div>
    );
  }

  const filtered = candidates.filter(c => c.score >= minScore);
  const isBk = (c: MatchCandidate) => Number(c.user_role) === 1;
  const visible = filtered.length > 0 && filtered.every(isBk) ? sortByAvailability(filtered) : filtered;
  if (visible.length === 0) {
    return <div style={{ fontSize: 12, color: t.muted, padding: '10px 0' }}>{tr('matching.list.empty')}</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: mode === 'compact' ? 4 : 8 }}>
      {visible.map(c => (
        <div
          key={c.id}
          onClick={onSelect ? () => onSelect(c) : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: mode === 'compact' ? '7px 10px' : '11px 14px',
            background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10,
            cursor: onSelect ? 'pointer' : 'default',
            position: 'relative',
          }}
          onMouseEnter={onSelect ? e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#b5297a'; } : undefined}
          onMouseLeave={onSelect ? e => { (e.currentTarget as HTMLDivElement).style.borderColor = t.borderSoft; } : undefined}
        >
          <Avatar name={c.name} userId={numericId(c.id)} size={mode === 'compact' ? 28 : 38} dark={dark} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: mode === 'compact' ? 12.5 : 13.5, fontWeight: 600, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.name} <span style={{ fontWeight: 400, color: t.muted, fontSize: 11 }}>#{c.id}</span>
            </div>
            {mode === 'compact' && isBk(c) && (
              <div data-startdatum={c.startdatum ?? ''} style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>
                {tr('matching.board.startdatum')}: <b>{formatStartdatum(c.startdatum) ?? tr('matching.board.startdatum_now')}</b>
              </div>
            )}
            {mode === 'full' && (
              <div style={{ fontSize: 11.5, color: t.muted, marginTop: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {isBk(c) && (
                  <span data-startdatum={c.startdatum ?? ''}>
                    {tr('matching.board.startdatum')}: <b>{formatStartdatum(c.startdatum) ?? tr('matching.board.startdatum_now')}</b>
                  </span>
                )}
                {c.ort && <span>{c.ort}</span>}
                {ageYears(c.geburtsdatum) != null && <span>{tr('matching.age_short', { years: ageYears(c.geburtsdatum) })}</span>}
                {c.pflegegrad != null && c.pflegegrad !== '' && <Badge tone="brand" dark={dark}>{tr('matching.pg_badge', { value: String(c.pflegegrad) })}</Badge>}
              </div>
            )}
            {/* Pflegeparameter auf einen Blick (Kacheln-PDF) */}
            {(c.icons?.length ?? 0) > 0 && (
              <div style={{ marginTop: mode === 'compact' ? 2 : 4 }}>
                <CareParamIconRow keys={c.icons!} dark={dark} size={mode === 'compact' ? 13 : 16} gap={mode === 'compact' ? 5 : 7} />
              </div>
            )}
          </div>
          {/* Score — Klick öffnet die Aufschlüsselung */}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setOpenBreakdown(openBreakdown === c.id ? null : c.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}
            title={tr('matching.breakdown.show')}
          >
            <MatchScoreBadge score={c.score} incomplete={c.incomplete} size={mode === 'compact' ? 'sm' : 'md'} />
          </button>
          {onSelect && mode === 'full' && (
            <Button variant="secondary" size="sm" dark={dark} onClick={() => onSelect(c)}>
              {tr('matching.list.select')}
            </Button>
          )}
          {openBreakdown === c.id && (
            <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, marginTop: 4 }}>
              <MatchDetailsPopover fields={c.fields} dark={dark} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
